import axios from 'axios';

import { BillingRecord, BillingRecordPatchObj } from '../interfaces/billing.interface';
import getAuthSession from './auth';
import { DataGridParams } from './user';

type InvoiceVariant = 'current' | 'mostRecent';

const NON_VARIANT_GRID_TO_API: Record<string, string> = {
  parentCompanyBillingCustomerId: 'parent_company_billing_customer_id',
};

const INVOICE_GRID_TO_API: Record<string, Record<InvoiceVariant, string>> = {
  updatedAt: {
    current: 'current_invoice_updated_at',
    mostRecent: 'invoice_updated_at',
  },

  invoiceStatus: {
    current: 'current_invoice_status',
    mostRecent: 'most_recent_invoice_status',
  },

  invoiceIsPaid: {
    current: 'current_invoice_is_paid',
    mostRecent: 'most_recent_invoice_is_paid',
  },

  paidDate: {
    current: 'current_invoice_paid_date',
    mostRecent: 'most_recent_invoice_paid_date',
  },

  item: {
    current: 'current_invoice_sku_item',
    mostRecent: 'most_recent_invoice_sku_item',
  },

  startDate: {
    current: 'current_invoice_start_date',
    mostRecent: 'most_recent_invoice_start_date',
  },

  endDate: {
    current: 'current_invoice_end_date',
    mostRecent: 'most_recent_invoice_end_date',
  },

  description: {
    current: 'current_invoice_sku_description',
    mostRecent: 'most_recent_invoice_sku_description',
  },

  itemTotal: {
    current: 'current_invoice_sku_item_total',
    mostRecent: 'most_recent_invoice_sku_item_total',
  },

  discountAmount: {
    current: 'current_invoice_discount_amount',
    mostRecent: 'most_recent_invoice_discount_amount',
  },

  invoiceTotal: {
    current: 'current_invoice_total',
    mostRecent: 'most_recent_invoice_total',
  },

  poNumber: {
    current: 'current_invoice_po_number',
    mostRecent: 'most_recent_invoice_po_number',
  },
};

export const mapGridToApiField = (gridField: string, showCurrentPeriodInvoices: boolean) => {
  const variant: InvoiceVariant = showCurrentPeriodInvoices ? 'current' : 'mostRecent';

  const invoiceVariantMap = INVOICE_GRID_TO_API[gridField];
  if (invoiceVariantMap) return invoiceVariantMap[variant];

  return NON_VARIANT_GRID_TO_API[gridField] ?? gridField;
};


export default async function getAllBillingRecords() {
  const authSession = await getAuthSession();
  const response = await axios.get(
    `${process.env.REACT_APP_BASE_API}/billing-records/?limit=99999`,
    {
      headers: { 'Authorization': `Bearer ${authSession.getIdToken().getJwtToken()}` },
    },
  );
  return response.data.results as BillingRecord[];
}

export async function getBillingRecord(billingCustomerId: string) {
  const authSession = await getAuthSession();
  const response = await axios.get(
    `${process.env.REACT_APP_BASE_API}/billing-records/?billing_customer_id=${billingCustomerId}`,
    {
      headers: { 'Authorization': `Bearer ${authSession.getIdToken().getJwtToken()}` },
    },
  );
  return response.data.results as BillingRecord[];
}

export async function patchBillingRecord(billingRecord: BillingRecordPatchObj) {
  const authSession = await getAuthSession();
  const response = await axios.patch(
    `${process.env.REACT_APP_BASE_API}/billing-records/${billingRecord.id}/`,
    { ...billingRecord },
    {
      headers: { 'Authorization': `Bearer ${authSession.getIdToken().getJwtToken()}` },
    },
  );
  return response.data as BillingRecord;
}

export async function processAdminBillingReportRequest(showCurrentInvoices: boolean) {
  const authSession = await getAuthSession();
  const token = authSession.getIdToken().getJwtToken();
  const requestUrl = showCurrentInvoices ?
    `${process.env.REACT_APP_BASE_API}/create-current-invoice-billing-record-report-task/` :
    `${process.env.REACT_APP_BASE_API}/create-send-most-recent-invoice-billing-record-report-task/`;

  const response = await axios.post(
    requestUrl,
    {},
    {
      headers: { Authorization: `Bearer ${token}` },
    },
  );

  return response.data;
}

export interface BillingRecordsDataGridResponse {
  results: BillingRecord[];
  count: number;
}

type GetBillingRecordsArgs = DataGridParams & {
  showCurrentPeriodInvoices: boolean;
};


export async function getBillingRecordsForDataGrid({
  page,
  pageSize,
  sortModel,
  filterModel,
  showCurrentPeriodInvoices,

}: GetBillingRecordsArgs): Promise<BillingRecordsDataGridResponse> {
  const authSession = await getAuthSession();
  const params: Record<string, any> = {
    limit: pageSize,
    offset: page * pageSize,
  };

  const sort = sortModel?.[0];
  if (sort?.field && sort?.sort) {
    const apiField = mapGridToApiField(sort.field, showCurrentPeriodInvoices);
    params.ordering = sort.sort === 'asc' ? apiField : `-${apiField}`;
  }

  if (filterModel?.items?.length) {
    filterModel.items.forEach((item) => {
      if (!item.value) return;

      const apiField = mapGridToApiField(item.columnField, showCurrentPeriodInvoices);

      // --- Special handling for boolean fields ---
      if (item.columnField === 'invoiceIsPaid') {
        params[apiField] = item.value === 'Yes' || item.value === true;
      } else if (item.columnField === 'company__is_in_grace_period') {
        params[apiField] = item.value === 'Yes' || item.value === true;
      } else {
        params[apiField] = item.value;
      }
    });
  }

  const response = await axios.get(
    `${process.env.REACT_APP_BASE_API}/billing-records/`,
    {
      headers: {
        'Authorization': `Bearer ${authSession.getIdToken().getJwtToken()}`,
      },
      params,
    },
  );

  return {
    results: response.data.results as BillingRecord[],
    count: response.data.count,
  };
}